import React from "react";
import { Link } from "wouter";
import { MapPin, Bookmark, User, Edit2, Trash2, Heart, Star, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { generateRouteSlug } from "@/lib/slugify";
import { getStaticMapUrl } from "@/lib/googleMaps";
import type { Route } from "@shared/schema";
import { InsiderStar } from "@/components/InsiderStar";
import { isInsider } from "@/lib/userTiers";

import { useState, useMemo } from "react";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useRouteInteractions } from "@/contexts/RouteInteractionsContext";
import { LoginModal } from "@/components/LoginModal";
import { useI18n } from "@/lib/i18n";
import { TranslatableText } from "@/components/TranslatableText";

import { RouteRatingModal } from "@/components/RouteRatingModal";
import { ShareModal } from "@/components/ShareModal";
import { useQuery } from "@tanstack/react-query";

interface RouteCardProps {
  route: Route;
  onSave?: () => void;
  onDelete?: (routeId: string) => void;
  showSaveButton?: boolean;
}

export function RouteCard({ route, onSave, onDelete, showSaveButton = true }: RouteCardProps) {
  const getOptimizedRouteImageUrl = (rawUrl: string | null): string | null => {
    if (!rawUrl) return null;
    try {
      // Reject legacy MapTiler URLs - consolidate on Geoapify
      if (rawUrl.includes("maptiler.com")) return null;

      if (rawUrl.includes("/storage/v1/object/public/")) {
        // Special case: route-thumbnails and share-images are already tailored for performance
        // (600x600 square, optimized buffers). Bypassing the transformation service (/render/...)
        // prevents stale caching and ensures ?v= versioning works reliably.
        if (rawUrl.includes("route-thumbnails") || rawUrl.includes("share-images")) {
          return rawUrl;
        }

        const [base, queryString] = rawUrl.split("?");
        const renderUrl = base.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
        const params = new URLSearchParams(queryString || "");
        // Updated to square dimensions 850x850 for Retina displays
        if (!params.has("width")) params.set("width", "850");
        if (!params.has("height")) params.set("height", "850");
        if (!params.has("quality")) params.set("quality", "80");
        if (!params.has("resize")) params.set("resize", "cover");
        return `${renderUrl}?${params.toString()}`;
      }
      return rawUrl;
    } catch {
      return rawUrl;
    }
  };

  const { t } = useI18n();
  const { isLoggedIn, user } = useAuth();
  const { toast } = useToast();
  const { getInteraction: getRouteInteraction, toggleFavorite: toggleRouteFavorite, toggleWishlist: toggleRouteWishlist } = useRouteInteractions();

  const routeInteractions = getRouteInteraction(route.id);
  const [isToggling, setIsToggling] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Fetch reviews count if not provided
  const { data: reviewData } = useQuery({
    queryKey: ["route-reviews", route.id],
    queryFn: async () => {
      const response = await fetch(`/api/routes/${route.id}/reviews`);
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!route.id
  });

  const reviewCount = reviewData?.reviews?.length ?? route.vintmap_review_count ?? 0;
  const averageRating = route.vintmap_rating ?? (reviewData?.reviews?.length > 0 ?
    reviewData.reviews.reduce((acc: number, r: any) => acc + r.rating, 0) / reviewData.reviews.length : 0);

  const handleToggleAction = async (e: React.MouseEvent, type: 'favorite' | 'wishlist') => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (isToggling) return;
    setIsToggling(true);

    try {
      if (type === 'favorite') {
        const newState = await toggleRouteFavorite(route.id);
        toast({
          title: newState ? "Toegevoegd aan favorieten" : "Verwijderd uit favorieten",
          duration: 2000,
        });
      } else {
        const newState = await toggleRouteWishlist(route.id);
        toast({
          title: newState ? "Toegevoegd aan bucketlist" : "Verwijderd uit bucketlist",
          duration: 2000,
        });
        onSave?.();
      }
    } catch (error) {
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Card className="hover-elevate overflow-hidden" data-testid={`card-route-${route.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0 flex-1">
            <h3 className="font-semibold text-base leading-tight truncate" data-testid={`text-route-name-${route.id}`}>
              {route.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">
                {route.allCities && route.allCities.length > 0
                  ? (route.allCities.length > 2 ? `${route.allCities.slice(0, 2).join(", ")} +${route.allCities.length - 2}` : route.allCities.join(", "))
                  : route.city}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <Badge variant="secondary" className="whitespace-nowrap">
              {t("routeDetail.stopsCount", { count: (route.stops?.length || route.shop_ids?.length || 0) })}
            </Badge>
            {(route.distance || (route as any).osm_route_data?.distance || (route as any).google_route_data?.distance) && (
              <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 whitespace-nowrap">
                {route.distance || (route as any).osm_route_data?.distance || (route as any).google_route_data?.distance}
              </Badge>
            )}
          </div>
        </div>

        {/* Route Thumbnail - with static map fallback */}
        {(() => {
          const [imageError, setImageError] = useState(false);

          // Filter out legacy MapTiler URLs to force use of new Geoapify static maps
          const cleanThumbnailUrl = route.thumbnail_url?.includes("maptiler.com") ? null : route.thumbnail_url;
          const cleanShareImageUrl = route.share_image_url?.includes("maptiler.com") ? null : route.share_image_url;

          const imgUrlRaw = cleanThumbnailUrl || cleanShareImageUrl || (
            route.stopsData && route.stopsData.length > 0
              ? getStaticMapUrl({
                path: (route as any).osm_route_data?.polyline || (route as any).google_route_data?.polyline,
                markers: route.stopsData
                  .map((s: any, idx: number) => ({
                    lat: s.lat || s.latitude,
                    lng: s.lng || s.longitude,
                    label: idx < 9 ? String(idx + 1) : undefined,
                    color: "0xF90F5A",
                  }))
                  .filter((s: any) => s.lat && s.lng),
                size: "420x420"
              })
              : null
          );
          const imgUrl = getOptimizedRouteImageUrl(imgUrlRaw);
          const finalImgUrl = imgUrl ? `${imgUrl}${imgUrl.includes('?') ? '&' : '?'}v=${new Date(route.updated_at || Date.now()).getTime()}` : null;

          return (finalImgUrl && !imageError) ? (
            <div className="relative aspect-square w-full rounded-lg overflow-hidden bg-muted">
              <img
                src={finalImgUrl}
                alt={`Route: ${route.name}`}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="relative aspect-square w-full rounded-lg overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-8 w-8 mx-auto text-primary/30 mb-1" />
                <p className="text-xs text-muted-foreground">
                  {t("routeDetail.stopsCount", { count: (route.stops?.length || route.shop_ids?.length || 0) })}
                </p>
              </div>
            </div>
          );
        })()}

        {route.description && (
          <TranslatableText
            table="routes"
            rowId={route.id}
            text={route.description}
            translations={(route as any).translations}
            className="text-sm text-muted-foreground line-clamp-2"
          />
        )}

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pt-2 border-t border-border">
          <div className="flex flex-col gap-1.5 text-xs text-muted-foreground min-w-0">
            <span className="flex items-center gap-1 min-w-0">
              <User className="h-3 w-3 flex-shrink-0" />
              <Link href={`/community?user=${encodeURIComponent(route.creatorUsername || "")}`} className="hover:underline cursor-pointer truncate max-w-[160px]">
                {route.creatorUsername}
              </Link>
              {isInsider(route.creatorUsername) && (
                <InsiderStar size={12} className="ml-0.5 flex-shrink-0" />
              )}
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1">
                <Bookmark className="h-3 w-3" />
                {(route.saves_count ?? 0) + (routeInteractions.wishlist ? 1 : 0)}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {route.likes_count ?? 0}
              </span>
              <button
                className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setShowRatingModal(true);
                }}
              >
                <Star className={`h-3 w-3 ${averageRating > 0 ? "fill-yellow-500 text-yellow-500" : ""}`} />
                <span className="font-medium">
                  {averageRating > 0 ? Number(averageRating).toFixed(1) : "0.0"}
                </span>
                <span className="opacity-70">
                  ({reviewCount > 0 ? `${reviewCount} ${t("rating.count") || "reviews"}` : t("rating.noReviews") || "geen reviews"})
                </span>
              </button>
            </div>
          </div>

          <div className="flex flex-col items-end w-full sm:w-auto gap-2">
            <div className="flex items-center gap-1 w-full justify-end">
              <div className="flex items-center bg-muted/20 rounded-full px-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 w-8 p-0 flex items-center justify-center rounded-full ${routeInteractions.favorite ? 'text-primary' : 'text-black'}`}
                  onClick={(e) => handleToggleAction(e, 'favorite')}
                  title={routeInteractions.favorite ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
                >
                  <Heart className={`h-4 w-4 ${routeInteractions.favorite ? 'fill-current' : ''}`} />
                </Button>

                {showSaveButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 flex items-center justify-center rounded-full ${routeInteractions.wishlist ? 'text-primary' : 'text-black'}`}
                    onClick={(e) => handleToggleAction(e, 'wishlist')}
                    data-testid={`button-save-route-${route.id}`}
                    title={routeInteractions.wishlist ? "Verwijder van bucketlist" : "Voeg toe aan bucketlist"}
                  >
                    <Bookmark className={`h-4 w-4 ${routeInteractions.wishlist ? 'fill-current' : ''}`} />
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex items-center justify-center rounded-full text-black hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShareModalOpen(true);
                  }}
                  title={t("share.titlePrefix")}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              {isLoggedIn && user?.id === route.user_id && (
                <div className="flex items-center gap-1">
                  <Link href={`/routes/edit/${route.id}`}>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Pas route aan">
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-primary hover:text-primary/80"
                    title="Verwijder route"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onDelete && confirm("Weet je zeker dat je deze route wilt verwijderen?")) {
                        onDelete(route.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <Link href={route.slug ? `/kringlooproutes/${route.slug}` : `/routes/${route.id}`} className="w-full">
              <Button size="sm" className="h-8 w-full px-3" data-testid={`button-view-route-${route.id}`}>
                {t("common.view")}
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} action="save" />
      <RouteRatingModal
        open={showRatingModal}
        onOpenChange={setShowRatingModal}
        routeId={route.id}
        routeName={route.name}
      />
      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={`${t("routeDetail.shareTitlePrefix")}: ${route?.name || ""}`}
        text={`${t("routeDetail.shareTextIntro")}\n\n🚗 ${route?.distance || (route as any).osm_route_data?.distance || (route as any).google_route_data?.distance || t("routeDetail.distanceUnknown")} • ${t("routeDetail.stopsCount", { count: (route.stops?.length || route.shop_ids?.length || 0) })}\n📍 ${route?.allCities?.join(", ") || route?.city || ""}\n\n${route?.description || ""}`}
        url={`${window.location.origin}${route.slug ? `/kringlooproutes/${route.slug}` : `/routes/${route.id}`}`}
        imageUrl={(route as any)?.share_image_url || undefined}
        entityType="route"
        entityId={route?.id}
      />
    </Card >
  );
}
